import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import Layout from 'components/layout';
import Link from 'components/link';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import ArrowForward from '@mui/icons-material/ArrowForward';
import LocationCityOutlined from '@mui/icons-material/LocationCityOutlined';
import ApartmentOutlined from '@mui/icons-material/ApartmentOutlined';
import HolidayVillageOutlined from '@mui/icons-material/HolidayVillageOutlined';
import DomainOutlined from '@mui/icons-material/DomainOutlined';
import BusinessOutlined from '@mui/icons-material/BusinessOutlined';
import TrendingUp from '@mui/icons-material/TrendingUp';
import TrendingDown from '@mui/icons-material/TrendingDown';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import { provinceOrStateNameFromCode } from 'utils/province_or_state';
import styles from './market-faq-page.module.scss';

import type { CountryCode } from 'types/countries';
import type { CityStats, TopCities } from 'data/insights/types';
import type { ProvinceOrStateCode } from 'utils/province_or_state';

export interface MarketFaqPageProps {
  countryCode: CountryCode;
  /** Pre-sorted (by province, then popularity) and pre-fetched server-side — no client fetch needed. */
  topCities: TopCities[];
  statsBySlug: Record<string, CityStats>;
}

// No photo source for market cards — rotate through a small icon pool,
// picked deterministically per city name, so the grid doesn't repeat one
// icon on every card (mock varies its photo art the same way, by hash).
const CARD_ICONS = [LocationCityOutlined, ApartmentOutlined, HolidayVillageOutlined, DomainOutlined, BusinessOutlined];

function cardIconForName(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  }
  return CARD_ICONS[hash % CARD_ICONS.length];
}

/** Month-over-month % change from the same InsightsResponse.medianPriceTrends the trend chart uses. */
function monthOverMonthChangePercent(stats?: CityStats): number | null {
  const trends = (stats?.medianPriceTrends ?? [])
    .filter(point => point?.medianPrice > 0)
    .slice()
    .sort((a, b) => a.date.localeCompare(b.date));
  if (trends.length < 2) {
    return null;
  }
  const latest = trends[trends.length - 1];
  const previous = trends[trends.length - 2];
  if (!previous.medianPrice) {
    return null;
  }
  return ((latest.medianPrice - previous.medianPrice) / previous.medianPrice) * 100;
}

/**
 * Market FAQ browse grid: search + province filters over a server-rendered,
 * already-populated list of top markets (province-grouped, ranked by
 * popularity within each province). Cards link straight to the market's
 * real city-info page (`/{slug}-info`) — that page already renders the full
 * Market FAQ snapshot (price tiles, gauge, price trend, etc.) via
 * MarketFaqAccordion, so there's no separate snapshot view here.
 */
export default function MarketFaqPage({ topCities, statsBySlug }: MarketFaqPageProps) {
  const { t } = useTranslation(Namespaces.COMMON);
  const { formatCompactPrice } = useI18nFormatting();

  const [search, setSearch] = useState('');
  const [provinceFilter, setProvinceFilter] = useState('all');

  // Same body-class technique the zoocasa-light homepage globe hero uses to
  // customize the shared header per-page — scoped to this route only, undone
  // on unmount so navigating away restores the normal header everywhere else.
  useEffect(() => {
    document.body.classList.add('market-faq-page');
    // html's background paints the overscroll/rubber-band region — without this
    // it stays on --themes-colors-primary-background (stark white on some
    // tenants), flashing above this page's off-white background when scrolling up.
    document.documentElement.style.backgroundColor = 'rgb(250, 250, 251)';
    return () => {
      document.body.classList.remove('market-faq-page');
      document.documentElement.style.backgroundColor = '';
    };
  }, []);

  const provinces = useMemo(
    () => Array.from(new Set(topCities.map(city => city.Province))),
    [topCities]
  );

  const filteredCities = useMemo(() => {
    const q = search.trim().toLowerCase();
    return topCities.filter(city =>
      (provinceFilter === 'all' || city.Province === provinceFilter) &&
      (!q || city.Name.toLowerCase().includes(q))
    );
  }, [topCities, search, provinceFilter]);

  const topTrendValue = useMemo(() => {
    const values = topCities.map(city => monthOverMonthChangePercent(statsBySlug[city.Slug])).filter((v): v is number => v !== null);
    return values.length > 0 ? Math.max(...values) : null;
  }, [topCities, statsBySlug]);

  return (
    <Layout showMobileBottomNav={false}>
      <div className={styles.container}>
        <div className={styles.homeView}>
          <div className={styles.hero}>
            <h1 className={styles.heroTitle}>{t(LOCALE_COMMON_PATHS.MARKETFAQ_HOME_HEROTITLE, { defaultValue: 'Explore local housing markets' })}</h1>
            <div className={styles.searchWrap}>
              <SearchOutlined className={styles.searchIcon} />
              <input
                className={styles.searchInput}
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t(LOCALE_COMMON_PATHS.MARKETFAQ_HOME_SEARCHPLACEHOLDER, { defaultValue: 'Search a city or neighbourhood…' })}
              />
            </div>
          </div>

          <div className={styles.filterLabel}>
            {search
              ? t(LOCALE_COMMON_PATHS.MARKETFAQ_HOME_MATCHINGMARKETS, { count: filteredCities.length, defaultValue: '{{count}} matching markets' })
              : t(LOCALE_COMMON_PATHS.MARKETFAQ_HOME_POPULARMARKETS, { defaultValue: 'Popular markets' })}
          </div>

          {provinces.length > 1 && (
            <div className={styles.filterRow}>
              <button
                className={provinceFilter === 'all' ? styles.filterPillActive : styles.filterPill}
                onClick={() => setProvinceFilter('all')}
              >
                {t(LOCALE_COMMON_PATHS.MARKETFAQ_HOME_ALLFILTER, { defaultValue: 'All' })}
              </button>
              {provinces.map(province => (
                <button
                  key={province}
                  className={provinceFilter === province ? styles.filterPillActive : styles.filterPill}
                  onClick={() => setProvinceFilter(province)}
                >
                  {provinceOrStateNameFromCode(province.toLowerCase() as ProvinceOrStateCode) || province}
                </button>
              ))}
            </div>
          )}

          <div className={styles.grid}>
            {filteredCities.map((city, index) => {
              const stats = statsBySlug[city.Slug];
              const trend = monthOverMonthChangePercent(stats);
              const isTrending = trend !== null && topTrendValue !== null && trend === topTrendValue;
              const CardIcon = cardIconForName(city.Name);
              return (
                <Link
                  key={city.Slug}
                  href={`/${city.Slug}-info`}
                  className={styles.card}
                  // Staggered entrance, capped so a long filtered list doesn't leave late cards waiting.
                  style={{ animationDelay: `${Math.min(index, 12) * 0.05}s` }}
                >
                  <div className={styles.cardTop}>
                    <span className={styles.cardIcon}><CardIcon fontSize="inherit" /></span>
                    {trend !== null && (
                      <span className={trend >= 0 ? styles.trendBadgeUp : styles.trendBadgeDown}>
                        {trend >= 0 ? <TrendingUp fontSize="inherit" /> : <TrendingDown fontSize="inherit" />}
                        {trend >= 0 ? '+' : ''}{trend.toFixed(1)}%
                        <span className={styles.trendTooltip}>{t(LOCALE_COMMON_PATHS.MARKETFAQ_HOME_TRENDTOOLTIP, { defaultValue: 'Change in median home price vs. last month' })}</span>
                      </span>
                    )}
                  </div>
                  {/* Always reserve this row's height so cards without a Trending tag don't
                      end up shorter and misaligned against cards that have one. */}
                  <div className={styles.trendingSlot}>
                    {isTrending && <span className={styles.trendingTag}>🔥 {t(LOCALE_COMMON_PATHS.MARKETFAQ_HOME_TRENDING, { defaultValue: 'Trending' })}</span>}
                  </div>
                  <div className={styles.cardName}>{city.Name}</div>
                  <div className={styles.cardRegion}>{city.Province}</div>
                  <div className={styles.cardFooter}>
                    <div>
                      <div className={styles.cardPrice}>
                        {stats?.MedianPurchasePrice ? formatCompactPrice(stats.MedianPurchasePrice) : '—'}
                      </div>
                      <div className={styles.cardMeta}>
                        {t(LOCALE_COMMON_PATHS.MARKETFAQ_HOME_MEDIANLISTINGS, { count: city.TotalCount, defaultValue: 'median · {{count}} listings' })}
                      </div>
                    </div>
                    <span className={styles.cardArrow}><ArrowForward fontSize="inherit" /></span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </Layout>
  );
}
